import React, { useEffect, useRef, useState, useCallback } from "react";
import { Modal, Button, Icon } from "semantic-ui-react";

// ── Session timeout configuration ────────────────────────────────────
export const IDLE_LIMIT = 2 * 60 * 60 * 1000;   // 2 h of inactivity → logout
export const WARN_BEFORE = 60 * 1000;            // warn 60s before idle logout
export const MAX_SESSION = 8 * 60 * 60 * 1000;   // 8 h absolute cap (active or not)
const CHECK_EVERY = 5 * 1000;                    // how often we evaluate the clock
const ACTIVITY_THROTTLE = 5 * 1000;              // min gap between last_activity writes

const LAST_ACTIVITY_KEY = "last_activity";
const SESSION_START_KEY = "session_start";
const SESSION_VERSION_KEY = "session_version";
const AUTH_KEY = "emp_id"; // presence of this == logged in

// Bump this on any deploy where you want ALL active sessions invalidated
// (everyone forced to re-login). Sessions whose stored version doesn't match
// — including every session created before this feature existed — are expired.
export const SESSION_VERSION = "1";

const ACTIVITY_EVENTS = [
    "mousemove",
    "mousedown",
    "keydown",
    "scroll",
    "touchstart",
    "click",
];

function now() {
    return new Date().getTime();
}

// Call this from the login success paths so a fresh login starts a clean clock.
export function startSessionClock() {
    const t = String(now());
    localStorage.setItem(SESSION_START_KEY, t);
    localStorage.setItem(LAST_ACTIVITY_KEY, t);
    localStorage.setItem(SESSION_VERSION_KEY, SESSION_VERSION);
}

// Same logout mechanism the rest of the app already uses.
function expireSession() {
    localStorage.clear();
    window.location.href = window.location.origin;
}

/**
 * Arms the idle/absolute session timers while `active` is true (i.e. logged in).
 * Returns state for rendering the warning modal.
 */
export function useSessionTimeout(active) {
    const [warningOpen, setWarningOpen] = useState(false);
    const [secondsToLogout, setSecondsToLogout] = useState(
        Math.round(WARN_BEFORE / 1000)
    );

    const lastWriteRef = useRef(0);
    const warningOpenRef = useRef(false);
    warningOpenRef.current = warningOpen;

    const touchActivity = useCallback(() => {
        const t = now();
        // Throttle localStorage writes so we don't thrash on every mousemove.
        if (t - lastWriteRef.current >= ACTIVITY_THROTTLE) {
            lastWriteRef.current = t;
            localStorage.setItem(LAST_ACTIVITY_KEY, String(t));
        }
        if (warningOpenRef.current) setWarningOpen(false);
    }, []);

    // "Stay signed in" → reset the idle clock and dismiss the modal.
    const staySignedIn = useCallback(() => {
        const t = now();
        lastWriteRef.current = t;
        localStorage.setItem(LAST_ACTIVITY_KEY, String(t));
        setWarningOpen(false);
    }, []);

    useEffect(() => {
        if (!active) return undefined;

        // Force re-login on deploy: any session whose stored version doesn't
        // match the current one (including pre-existing sessions with none) is
        // invalidated immediately.
        if (localStorage.getItem(SESSION_VERSION_KEY) !== SESSION_VERSION) {
            expireSession();
            return undefined;
        }

        // Make sure timestamps exist (e.g. on a page reload of an existing session).
        if (!localStorage.getItem(SESSION_START_KEY)) {
            localStorage.setItem(SESSION_START_KEY, String(now()));
        }
        if (!localStorage.getItem(LAST_ACTIVITY_KEY)) {
            localStorage.setItem(LAST_ACTIVITY_KEY, String(now()));
        }

        ACTIVITY_EVENTS.forEach((evt) =>
            window.addEventListener(evt, touchActivity, { passive: true })
        );

        // Cross-tab sync: if the session is cleared in another tab, expire here too.
        const onStorage = (e) => {
            if (e.key === AUTH_KEY && e.newValue == null) {
                window.location.href = window.location.origin;
            }
        };
        window.addEventListener("storage", onStorage);

        const tick = setInterval(() => {
            const lastActivity = Number(
                localStorage.getItem(LAST_ACTIVITY_KEY) || 0
            );
            const sessionStart = Number(
                localStorage.getItem(SESSION_START_KEY) || 0
            );
            const t = now();
            const idle = t - lastActivity;
            const age = t - sessionStart;

            if (idle >= IDLE_LIMIT || age >= MAX_SESSION) {
                expireSession();
                return;
            }

            if (idle >= IDLE_LIMIT - WARN_BEFORE) {
                const remaining = Math.max(
                    0,
                    Math.round((IDLE_LIMIT - idle) / 1000)
                );
                setSecondsToLogout(remaining);
                if (!warningOpenRef.current) setWarningOpen(true);
            } else if (warningOpenRef.current) {
                setWarningOpen(false);
            }
        }, CHECK_EVERY);

        return () => {
            ACTIVITY_EVENTS.forEach((evt) =>
                window.removeEventListener(evt, touchActivity)
            );
            window.removeEventListener("storage", onStorage);
            clearInterval(tick);
        };
    }, [active, touchActivity]);

    return { warningOpen, secondsToLogout, staySignedIn };
}

export function SessionTimeoutModal({ warningOpen, secondsToLogout, staySignedIn }) {
    return (
        <Modal open={warningOpen} size="mini" dimmer="blurring">
            <Modal.Header>
                <Icon name="clock outline" color="orange" /> Session expiring
            </Modal.Header>
            <Modal.Content>
                <p>
                    You've been inactive. You'll be logged out in{" "}
                    <b>{secondsToLogout}s</b>.
                </p>
            </Modal.Content>
            <Modal.Actions>
                <Button color="orange" onClick={staySignedIn}>
                    <Icon name="checkmark" /> Stay signed in
                </Button>
            </Modal.Actions>
        </Modal>
    );
}
